/**
 * Shared URL extraction and normalization utilities.
 * Used by RichComposer for highlighting and useLinkCard for card detection.
 */
import { isSafeUrl } from '@mackinac/shared'

export { isSafeUrl }

/** Match URLs with explicit scheme (https://example.com/path) — global for use with replace() */
export const SCHEME_URL_REGEX = /https?:\/\/[^\s]+/g

/**
 * Match schemeless URLs (example.com, sub.domain.io/path) — global for use with replace()
 * Must have at least one dot and valid TLD (2+ chars).
 *
 * Two negative lookbehinds keep this from matching parts of @mentions or #hashtags:
 *  - `(?<![#])` skips a domain glued to a `#` (e.g. the start of a hashtag).
 *  - `(?<!@[a-zA-Z0-9.-]*)` skips any domain that belongs to an @mention. A handle
 *    like `@user.bsky.social` is one contiguous token, so without this the regex would
 *    happily start matching at the inner `bsky.social` segment (preceded only by a `.`)
 *    and surface a bogus link card. The variable-length lookbehind walks back across the
 *    whole handle to the leading `@` and rejects the match.
 */
export const SCHEMELESS_URL_REGEX =
    /(?<![#])(?<!@[a-zA-Z0-9.-]*)\b(?:[a-zA-Z0-9][-a-zA-Z0-9]{0,61}[a-zA-Z0-9]?\.)+[a-zA-Z]{2,}\b(?:\/[^\s]*)?/g

// Non-global versions for extractFirstUrl (avoids lastIndex statefulness)
const SCHEME_URL_MATCH = /https?:\/\/[^\s]+/
const SCHEMELESS_URL_MATCH =
    /(?<![#])(?<!@[a-zA-Z0-9.-]*)\b(?:[a-zA-Z0-9][-a-zA-Z0-9]{0,61}[a-zA-Z0-9]?\.)+[a-zA-Z]{2,}\b(?:\/[^\s]*)?/

/**
 * Extract the first URL from text by position.
 * Returns whichever URL appears earliest, regardless of scheme.
 */
export function extractFirstUrl(text: string): string | null {
    const schemeMatch = SCHEME_URL_MATCH.exec(text)
    const schemelessMatch = SCHEMELESS_URL_MATCH.exec(text)

    if (!schemeMatch && !schemelessMatch) return null
    if (!schemeMatch) return schemelessMatch![0]
    if (!schemelessMatch) return schemeMatch[0]

    // Return whichever appears first in the text
    return schemeMatch.index <= schemelessMatch.index ? schemeMatch[0] : schemelessMatch[0]
}

/**
 * Normalize a URL by prepending https:// if no scheme is present.
 */
export function normalizeUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) return url
    return `https://${url}`
}

/**
 * Opens a URL in a new tab, guarded by isSafeUrl. Returns whether the URL
 * was safe and window.open was called, so callers can fall back (e.g. to
 * an in-app route) when it wasn't.
 */
export function openExternalUrl(url: string | null | undefined): boolean {
    if (!isSafeUrl(url)) return false
    window.open(url!, '_blank', 'noopener,noreferrer')
    return true
}
